import { EventEmitter, Injectable } from "@angular/core";
import {
  OneSignal, OSDisplayType, OSNotification, OSNotificationOpenedResult,
} from "@ionic-native/onesignal";
import { AppConfig } from '@app/config'
import { ApiCollectionResponse, ApiViewData, NavigationParams, Notification, User } from "../app/app.interfaces";
import { ApiService } from "./api.service";
import { AuthService } from "./auth.service";
import { NavigationHelper } from "../helpers/navigation.helper";
import { Platform } from "ionic-angular";
import { EventService } from "./event.service";
import { IonicHelper } from "../helpers/ionic.helper";


const OSNotificationPermission = {
    NotDetermined: 0,
    Authorized: 1,
    Denied: 2
};


export interface OneSignalId {
  userId: string;
  pushToken: string;
}
export interface NotificationData {
  navigate?: NavigationParams;
  notificationId?: number;
}

@Injectable()
export class NotificationService {
  
  private oneSignalId: OneSignalId;
  
  public notifications = [];
  _view: ApiViewData = {};
  
  set view(view: ApiViewData){
    this._view = view;
    // this.viewChange.emit(view);
  }
  get view(){
    return this._view;
  }
    
  public onLoad: EventEmitter<void> = new EventEmitter<void>();
  public onPrepend: EventEmitter<void> = new EventEmitter<void>();
  public onAppend: EventEmitter<void> = new EventEmitter<void>();
  public onUnreadChanged: EventEmitter<void> = new EventEmitter<void>();
  
  
  private loadingIntervalId: any = null;
  
  constructor(private oneSignal: OneSignal, 
              private apiService: ApiService, 
              private authService: AuthService,
              private eventService: EventService,
              private navigationHelper: NavigationHelper,
              private ionic: IonicHelper
              ){
    
  }

  public init(platform: Platform): void {
        
    if(!this.authService.user) {
      throw new Error('Not logged in');
    }
            
    this.loadNotificationsFromApi();
        
    if(platform.is('cordova') && (platform.is('ios') || platform.is('android'))) {
      this.initOneSignal();
    }
    else {
      this.loadingIntervalId = setInterval(()=>{
        if(this.apiService.isConnected) this.loadNotificationsFromApi(false, true);
      }, 60000);      
    }    
    
    //Mark notification from url as read
    const urlNotificationId = this.getQueryParameter('n');    
    if(urlNotificationId!==null){
      this.markNotificationAsRead(urlNotificationId);
    }
    
  }
  
  public get firstNotifications(){
    return this.notifications.slice(0,5);
  }
  
  loadMore(): void {   
    this.loadNotificationsFromApi(true, false, this.view.pages.next);
  }

  
  private loadNotificationsFromApi(append: boolean = false, prepend: boolean = false, uri = 'user_notifications'): void {    
    
    this.view.loading = true;
    
    this.apiService.getCollection(uri).then((apiResponse: ApiCollectionResponse) => {
      
      let notificationsChanged: boolean = false;
      
      if(append) {
        for(let item of apiResponse.items) {
          //Check if existing        
          if(!this.notifications.find(notification => notification.id === item.id)) {
            this.notifications.push(item);
            notificationsChanged = true;
          }
        }
        this.view = Object.assign({}, apiResponse.view, { loading: false });    
        
        if(notificationsChanged) {
          this.onAppend.emit();
        }
      }
      else if (prepend){
        for(let item of apiResponse.items.reverse()) {
          //Check if existing
          if(!this.notifications.find(notification => notification.id === item.id)) {
            this.notifications.unshift(item);
            notificationsChanged = true;
          }
        }        
        this.view = Object.assign({}, this.view, { loading: false });
        
        if(notificationsChanged){
          this.onPrepend.emit();
          this.onUnreadChanged.emit();
        }
      }
      else {
        this.notifications = apiResponse.items;        
        this.view = Object.assign({}, apiResponse.view, { loading: false });
        this.onLoad.emit();
        this.onUnreadChanged.emit();
      }
      

    }).catch((err)=>{
      console.error(err);
      this.view.loading = false;
      //niks
    });    
  }
  
  
  
  public markAllNotificationsAsRead() {
    this.notifications = this.notifications.map(notification=>{notification.read=true; return notification});
    this.onUnreadChanged.emit();
    return this.apiService.customPost('user/notifications/mark-as-read/all').catch((err) => {
      console.error(err);    	
    });
  }
  
  public markNotificationAsRead(notificationId) {  
    let notification = this.notifications.find(notification => notification.id === notificationId);
    if(notification) notification.read = true;   
    this.onUnreadChanged.emit();   
    return this.apiService.customPost('user/notifications/mark-as-read/'+notificationId).catch((err) => {
      console.error(err);    	
    });
  } 
  
  private determineAndRegisterOneSignalId(){
    
    this.oneSignal.getIds().then((oneSignalId: OneSignalId) => {
      if(oneSignalId!==null && oneSignalId!==undefined) {
        this.oneSignalId = oneSignalId;
        this.registerUserOneSignalId();
      }
    });    
  }
  
  private initOneSignal(): void {
    
    // this.oneSignal.setLogLevel({logLevel:6, visualLevel: 1});
        
    this.oneSignal.addPermissionObserver().subscribe((res: any) => {
      if(res.to && res.to.status && res.to.status === OSNotificationPermission.Authorized) {        
        this.determineAndRegisterOneSignalId();
        // this.verifyAndRegisterOneSignalEmailAddress();
      }
      else {
        if(this.oneSignalId !== null && this.oneSignalId !== undefined) {
          this.unregisterUserOneSignalId();
        }
      }      
    });
    
    this.oneSignal.startInit(AppConfig.oneSignalAppId, AppConfig.googleSenderId);
    this.oneSignal.inFocusDisplaying(OSDisplayType.None);
    
    
    this.determineAndRegisterOneSignalId();
    // this.verifyAndRegisterOneSignalEmailAddress();
    
    this.oneSignal.handleNotificationReceived().subscribe((notification: OSNotification) => {
      // do something when notification is received
      // console.log('Notification received: ', notification);
      this.loadNotificationsFromApi(false, true);
    });
    
    this.oneSignal.handleNotificationOpened().subscribe((notificationOpened: OSNotificationOpenedResult) => {
      // do something when a notification is opened
      // console.log('Notification openened: ', notificationOpened);

      const notification = notificationOpened.notification;

      if(notification.payload && notification.payload.additionalData) {
        const additionalData: NotificationData = notification.payload.additionalData;
        
        this.markNotificationAsRead(additionalData.notificationId);
        
        if (additionalData.navigate) {
          const navPath = this.navigationHelper.generatePathFromNavigationParams(additionalData.navigate);
          this.navigationHelper.pushWithLocationUpdate(navPath, additionalData.navigate.route, additionalData.navigate.params);
        }          

      }

    });
    
    this.oneSignal.endInit();
        
    this.authService.userLogout.subscribe((user: User) => {
      
      if(this.loadingIntervalId) clearInterval(this.loadingIntervalId);
      
      if(this.oneSignalId !== null && this.oneSignalId !== undefined) {
        this.unregisterUserOneSignalId();
        //this.unregisterOneSignalEmail();
      }
    });
    
  }
  
  
  
  private registerUserOneSignalId(): Promise<any> {        
    return this.apiService.customPost('user/onesignal/register', { oneSignalUserId: this.oneSignalId.userId }).then((res: any)=> {      

    }).catch((err) => {
      console.error(err);    	
    });
  }
  
  private unregisterUserOneSignalId(): Promise<any> {        
    return this.apiService.customPost('user/onesignal/unregister', { oneSignalUserId: this.oneSignalId.userId }).then((res)=> {      
      this.oneSignalId = null;
    }).catch((err) => {
      console.error(err);    	
    });    
  }
  
  public get unreadNotificationCount(){
    return this.notifications.filter((notification) => !notification.read).length;
  }
    
  private unregisterOneSignalEmail(): any {        
    return (window as any).plugins.OneSignal.logoutEmail();
  }
  
  private verifyAndRegisterOneSignalEmailAddress(): Promise<any>{        
    return new Promise<any>((resolve) => {      
      this.apiService.customGet('user/onesignal/email-hash').then((result) => {        
        (window as any).plugins.OneSignal.setEmail(result.email, result.hash, function(res){
          resolve();
        }, function(err){
          console.error(err);
          //reject();
        });        
      }).catch(err=> {
        console.error(err);
      })
    });
  }
  
  private getQueryParameter(name) {
      let url = window.location.href;
      name = name.replace(/[\[\]]/g, "\\$&");
      let regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
          results = regex.exec(url);
      if (!results) return null;
      if (!results[2]) return '';
      return decodeURIComponent(results[2].replace(/\+/g, " "));
  }

}


// WEBPACK FOOTER //
// ./src/services/notification.service.ts